/**
 * HelperEditor — popover for authoring a "Helfer" (formula-backed virtual
 * column) during the column-mapping wizard step.
 *
 * Helpers don't persist to the DB; they materialize per-row in the diff view
 * and, in export flows, as extra columns in the downloaded file.  The editor
 * shows a label input, a formula input, and a click-to-insert reference
 * picker listing everything the helper can reference: primitives already
 * mapped on this tab, other helpers on this tab, rollup columns on this tab,
 * and (when a document is present) document column headers as `[Header]`.
 */

import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import clsx from "clsx"
import { useMemo, useState } from "react"
import {
  evaluateFormula,
  extractReferences,
  isValidHelperLabel,
} from "../../utils/formula"

export interface HelperRef {
  /** Identifier that resolves bare-token references (primitive names, helper
   *  labels, rollup labels). */
  identifier: string
  /** Human-facing description shown in the picker. */
  title: string
  /** Group label so the picker can cluster refs by source. */
  group: string
}

export interface HelperEditorInitial {
  label: string
  expression: string
  targetDocHeader?: string | null
}

export function HelperEditor({
  initial,
  existingLabels,
  availableRefs,
  docHeaders,
  disabledDocHeaders = [],
  onSave,
  onCancel,
}: {
  /** Pre-filled values when editing an existing helper. */
  initial?: HelperEditorInitial
  /** Labels already used on THIS tab (excluding the one being edited) so we
   *  can reject duplicate helper names. */
  existingLabels: string[]
  /** Bare-identifier references the formula can use. */
  availableRefs: HelperRef[]
  /** Document column headers; when non-empty the editor exposes them as
   *  `[Header]` bracket tokens. */
  docHeaders: string[]
  /** Doc headers already claimed by another mapping/helper — greyed out in
   *  the target dropdown so a single doc column can't be exported twice. */
  disabledDocHeaders?: string[]
  onSave: (
    label: string,
    expression: string,
    targetDocHeader: string | null,
  ) => void
  onCancel: () => void
}) {
  const { translate } = useTranslation()
  const [label, setLabel] = useState(initial?.label ?? "")
  const [expression, setExpression] = useState(initial?.expression ?? "")
  /** '' = new column (use label); any string = assign to that doc header. */
  const [target, setTarget] = useState<string>(initial?.targetDocHeader ?? "")

  // Auto-derive a safe identifier label from a doc header so the user doesn't
  // have to type one — they can still override it if needed.
  function slugifyHeader(h: string): string {
    const slug = h
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
    return /^[a-z_]/.test(slug) ? slug : `h_${slug}`
  }

  const labelValid = isValidHelperLabel(label)
  const labelClash = existingLabels.includes(label)

  const labelError = !label
    ? null
    : !labelValid
      ? translate("QMS/HELPER/LABEL_INVALID")
      : labelClash
        ? translate("QMS/HELPER/LABEL_DUPLICATE")
        : null

  // Dry-run eval the expression with 1 as every reference so the user sees
  // structural errors (missing refs / bad syntax) while typing.
  const exprPreview = useMemo(() => {
    if (!expression.trim()) return { value: null, error: null as string | null }
    const refs = extractReferences(expression)
    const identifiers: Record<string, string> = {}
    for (const r of refs.identifiers) {
      const available = availableRefs.find((a) => a.identifier === r)
      identifiers[r] = available ? "1" : ""
    }
    const docCells: Record<string, string> = {}
    for (const h of refs.docHeaders)
      docCells[h] = docHeaders.includes(h) ? "1" : ""
    const result = evaluateFormula(
      expression,
      { identifiers, docCells },
      translate,
    )
    // Shape errors we care about: missing reference / bad syntax.
    return { value: result.value, error: result.error }
  }, [expression, availableRefs, docHeaders, translate])

  const canSave =
    !!label &&
    labelValid &&
    !labelClash &&
    !!expression.trim() &&
    !exprPreview.error

  function insertAtCaret(token: string) {
    setExpression((e) => (e.trim() ? `${e.trimEnd()} ${token} ` : `${token} `))
  }

  const groupedRefs = useMemo(() => {
    const groups: Record<string, HelperRef[]> = {}
    for (const r of availableRefs) {
      if (!groups[r.group]) groups[r.group] = []
      groups[r.group].push(r)
    }
    return groups
  }, [availableRefs])

  return (
    <div className="absolute right-0 top-full z-10 mt-1 w-104 rounded-lg border border-success/40 bg-grey2 p-3 shadow-lg">
      <div className="mb-2">
        <label className="mb-1 block text-[10px] uppercase tracking-wide text-grey5">
          {translate("QMS/HELPER/OUTPUT_LABEL")}
        </label>
        <select
          value={target}
          onChange={(e) => {
            const v = e.target.value
            setTarget(v)
            // When picking a doc header, auto-derive a valid identifier so
            // other helpers can still reference this one by name.
            if (v) setLabel(slugifyHeader(v))
          }}
          className="w-full rounded border border-grey4 bg-grey1 px-2 py-1 text-xs text-white focus:border-success focus:outline-hidden"
        >
          <option value="">{translate("QMS/HELPER/NEW_COLUMN_OPTION")}</option>
          {docHeaders.map((h) => {
            const taken = disabledDocHeaders.includes(h)
            return (
              <option key={h} value={h} disabled={taken}>
                {translate("QMS/HELPER/DOC_COLUMN_OPTION", { header: h })}
                {taken ? ` ${translate("QMS/HELPER/DOC_COLUMN_TAKEN")}` : ""}
              </option>
            )
          })}
        </select>
        <p className="mt-0.5 text-[10px] text-grey5">
          {target
            ? translate("QMS/HELPER/EXPORT_MODE_HELP", { target })
            : translate("QMS/HELPER/IMPORT_MODE_HELP")}
        </p>
      </div>

      <div className="mb-2">
        <label className="mb-1 block text-[10px] uppercase tracking-wide text-grey5">
          Name
        </label>
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder={translate("QMS/HELPER/NAME_PLACEHOLDER")}
          className={clsx(
            "font-mono w-full rounded border bg-grey1 px-2 py-1 text-xs text-white focus:outline-hidden",
            labelError ? "border-red1" : "border-grey4 focus:border-success",
          )}
          autoFocus
        />
        {labelError && (
          <p className="mt-0.5 text-[10px] text-red1">{labelError}</p>
        )}
        {target && !labelError && (
          <p className="mt-0.5 text-[10px] text-grey5">
            {translate("QMS/HELPER/IDENTIFIER_HELP")}
          </p>
        )}
      </div>

      <div className="mb-2">
        <label className="mb-1 block text-[10px] uppercase tracking-wide text-grey5">
          {translate("QMS/HELPER/FORMULA_LABEL")}
        </label>
        <input
          type="text"
          value={expression}
          onChange={(e) => setExpression(e.target.value)}
          placeholder={translate("QMS/HELPER/FORMULA_PLACEHOLDER")}
          className={clsx(
            "font-mono w-full rounded border bg-grey1 px-2 py-1 text-xs text-white focus:outline-hidden",
            exprPreview.error
              ? "border-red1"
              : "border-grey4 focus:border-success",
          )}
        />
        {exprPreview.error && (
          <p className="mt-0.5 text-[10px] text-red1">{exprPreview.error}</p>
        )}
        {!exprPreview.error && expression && (
          <p className="mt-0.5 text-[10px] text-grey5">
            {translate("QMS/HELPER/VALID_STRUCTURE")}
          </p>
        )}
      </div>

      <div className="mb-3">
        <p className="mb-1 text-[10px] uppercase tracking-wide text-grey5">
          {translate("QMS/HELPER/INSERT_REFERENCES")}
        </p>
        <div className="max-h-32 overflow-y-auto rounded border border-grey4 bg-grey1 p-1.5">
          {Object.entries(groupedRefs).map(([group, refs]) => (
            <div key={group} className="mb-1.5 last:mb-0">
              <div className="mb-0.5 text-[9px] uppercase text-grey5">
                {group}
              </div>
              <div className="flex flex-wrap gap-1">
                {refs.map((r) => (
                  <button
                    key={r.identifier}
                    type="button"
                    onClick={() => insertAtCaret(r.identifier)}
                    className="font-mono rounded border border-grey4 px-1.5 py-0.5 text-[10px] text-grey6 hover:border-success hover:bg-success/10"
                    title={r.title}
                  >
                    {r.identifier}
                  </button>
                ))}
              </div>
            </div>
          ))}
          {docHeaders.length > 0 && (
            <div className="mb-1.5 last:mb-0">
              <div className="mb-0.5 text-[9px] uppercase text-grey5">
                {translate("QMS/HELPER/DOC_COLUMNS_LABEL")}
              </div>
              <div className="flex flex-wrap gap-1">
                {docHeaders.map((h) => (
                  <button
                    key={h}
                    type="button"
                    onClick={() => insertAtCaret(`[${h}]`)}
                    className="font-mono rounded border border-grey4 px-1.5 py-0.5 text-[10px] text-grey6 hover:border-success hover:bg-success/10"
                    title={translate("QMS/HELPER/DOC_CELL_TITLE", {
                      header: h,
                    })}
                  >
                    [{h}]
                  </button>
                ))}
              </div>
            </div>
          )}
          {availableRefs.length === 0 && docHeaders.length === 0 && (
            <p className="text-[10px] text-grey5">
              {translate("QMS/HELPER/NO_REFERENCES")}
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-2.5 py-1 text-[11px] text-grey5 hover:text-white"
        >
          {translate("QMS/COMMON/CANCEL")}
        </button>
        <button
          type="button"
          onClick={() =>
            canSave && onSave(label, expression.trim(), target || null)
          }
          disabled={!canSave}
          className={clsx(
            "rounded border px-3 py-1 text-[11px] font-medium transition-colors",
            canSave
              ? "border-success/50 bg-success/20 text-success hover:bg-success/30"
              : "cursor-not-allowed border-grey4 text-grey5 opacity-50",
          )}
        >
          {translate("QMS/COMMON/SAVE")}
        </button>
      </div>
    </div>
  )
}
